/**
 * ZIP (STORE method — uncompressed) building for the .xlsx writer.
 *
 * Lifted verbatim from apps/ledger-web/src/lib/export_xlsx.ts. STORE is valid
 * per the ZIP spec and accepted by every Office/LibreOffice/Google Sheets
 * parser, so the writer needs no DEFLATE dependency.
 */

const UTF8_ENC = new TextEncoder();
export const toUtf8 = (s: string): Uint8Array => UTF8_ENC.encode(s);

export function concatBufs(bufs: Uint8Array[]): Uint8Array {
  const total = bufs.reduce((n, b) => n + b.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const b of bufs) {
    out.set(b, off);
    off += b.length;
  }
  return out;
}

const CRC32_TABLE: Uint32Array = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c;
  }
  return t;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) crc = CRC32_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function u16le(val: number): Uint8Array {
  return new Uint8Array([val & 0xff, (val >> 8) & 0xff]);
}

function u32le(val: number): Uint8Array {
  return new Uint8Array([
    val & 0xff,
    (val >> 8) & 0xff,
    (val >> 16) & 0xff,
    (val >>> 24) & 0xff,
  ]);
}

interface ZipEntry {
  nameBytes: Uint8Array;
  data: Uint8Array;
  crc: number;
  offset: number;
}

/** Build a STORE-method ZIP from a set of named byte payloads. */
export function buildZip(
  files: { name: string; data: Uint8Array }[],
): Uint8Array {
  const entries: ZipEntry[] = [];
  const localParts: Uint8Array[] = [];
  let offset = 0;

  for (const f of files) {
    const nameBytes = toUtf8(f.name);
    const entryCrc = crc32(f.data);

    const lfh = concatBufs([
      new Uint8Array([0x50, 0x4b, 0x03, 0x04]),
      u16le(20),
      u16le(0),
      u16le(0),
      u16le(0),
      u16le(0),
      u32le(entryCrc),
      u32le(f.data.length),
      u32le(f.data.length),
      u16le(nameBytes.length),
      u16le(0),
      nameBytes,
    ]);

    entries.push({ nameBytes, data: f.data, crc: entryCrc, offset });
    localParts.push(lfh, f.data);
    offset += lfh.length + f.data.length;
  }

  const cdParts: Uint8Array[] = entries.map((e) =>
    concatBufs([
      new Uint8Array([0x50, 0x4b, 0x01, 0x02]),
      u16le(20),
      u16le(20),
      u16le(0),
      u16le(0),
      u16le(0),
      u16le(0),
      u32le(e.crc),
      u32le(e.data.length),
      u32le(e.data.length),
      u16le(e.nameBytes.length),
      u16le(0),
      u16le(0),
      u16le(0),
      u16le(0),
      u32le(0),
      u32le(e.offset),
      e.nameBytes,
    ]),
  );

  const cdBytes = concatBufs(cdParts);
  const cdOffset = offset;

  const eocd = concatBufs([
    new Uint8Array([0x50, 0x4b, 0x05, 0x06]),
    u16le(0),
    u16le(0),
    u16le(entries.length),
    u16le(entries.length),
    u32le(cdBytes.length),
    u32le(cdOffset),
    u16le(0),
  ]);

  return concatBufs([...localParts, cdBytes, eocd]);
}
